import React, { useContext, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { ProjectsContext } from "../../context/projectsContext";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import {
  MDBRow,
  MDBCol,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalBody,
} from "mdb-react-ui-kit";
import ReactPlayer from "react-player";
import { Contact } from "../../containers";
import BackArrow from "../../assets/back.svg";
import LeftArrow from "../../assets/left.png";
import RightArrow from "../../assets/right.png";
import playIcon from "../../assets/play.svg";
import LinkIcon from "../../assets/link.svg";
import "./viewProject.css";
import { useTheme } from "../../context/ThemeContext";

const ViewProject = () => {
  const { projects } = useContext(ProjectsContext);
  const { name } = useParams();
  const formattedName = name.replace(/-/g, " ");
  const [icons, setIcons] = useState([]);
  const [modal, setModal] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [playingVideos, setPlayingVideos] = useState({});
  const { isDark } = useTheme();

  // Memorise icons for faster lookup
  const iconsMap = useMemo(() => {
    const map = {};
    icons.forEach((icon) => {
      map[icon.name.toLowerCase()] = icon.svg
        .replace(/fill="[^"]*"/g, 'fill="currentColor"')
        .replace(/stroke="[^"]*"/g, 'stroke="currentColor"');
    });
    return map;
  }, [icons]);

  // Fetch icons from Firestore
  useEffect(() => {
    const getIcons = async () => {
      try {
        const iconsCollection = collection(db, "icons");
        const iconsSnapshot = await getDocs(iconsCollection);
        const iconsList = iconsSnapshot.docs.map((doc) => doc.data());
        setIcons(iconsList);
      } catch (error) {
        console.error("Error fetching icons:", error);
      }
    };
    getIcons();
  }, []);

  const selectedProject = projects.find(
    (project) => project.name.toLowerCase() === formattedName
  );

  if (!selectedProject) return <div>Project not found</div>;

  const { description, category, software, gallery, videoUrl, url } =
    selectedProject;

  const selectedMedia = gallery[selectedMediaIndex];

  // Softwares with icons
  const softwaresUsed = software.map((softwareItem) => {
    const svg = iconsMap[softwareItem.toLowerCase()];
    return svg ? (
      <div
        key={softwareItem}
        className="software-icon"
        style={{ color: "var(--text-primary)" }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    ) : (
      <div key={softwareItem} className="software-icon">
        {softwareItem}
      </div>
    );
  });

  const toggleModal = () => setModal(!modal);

  const handleNextMedia = () =>
    setSelectedMediaIndex((prev) => (prev + 1) % gallery.length);

  const handlePrevMedia = () =>
    setSelectedMediaIndex(
      (prev) => (prev - 1 + gallery.length) % gallery.length
    );

  const handleMediaClick = (index) => {
    if (!isVideo(gallery[index])) {
      setSelectedMediaIndex(index);
      const img = new Image();
      img.src = gallery[index];
      img.onload = () => setModal(true);
    }
  };

  const isVideo = (file) => file.includes(".mp4");

  const handlePlayPause = (index) =>
    setPlayingVideos((prev) => ({ ...prev, [index]: !prev[index] }));

  const handleVideoPlay = (index) =>
    setPlayingVideos((prev) => ({ ...prev, [index]: true }));

  const handleVideoPause = (index) =>
    setPlayingVideos((prev) => ({ ...prev, [index]: false }));

  // Safe YouTube ID extraction
  const getYouTubeId = (url) => {
    const match = url.match(/[?&]v=([^&]+)/);
    return match ? match[1] : null;
  };

  const youTubeId = videoUrl ? getYouTubeId(videoUrl) : null;

  return (
    <section className="view-project container">
      {/* Modal for images */}
      <MDBModal
        show={modal}
        className="modal-image"
        onHide={toggleModal}
        tabIndex="-1"
        setShow={setModal}
      >
        <MDBModalDialog size="lg">
          <MDBModalContent>
            <MDBModalHeader>
              <button
                type="button"
                className="btn-close"
                onClick={toggleModal}
              ></button>
            </MDBModalHeader>
            <MDBModalBody className="modal-body d-flex align-items-center justify-content-between">
              <button onClick={handlePrevMedia}>
                <img src={LeftArrow} alt="Previous" />
              </button>
              <img
                src={selectedMedia}
                alt="Enlarged project"
                className="media"
                loading="lazy"
                style={{ borderRadius: "10px", maxWidth: "80%" }}
              />
              <button onClick={handleNextMedia}>
                <img src={RightArrow} alt="Next" />
              </button>
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <div className="project-info pb-5">
        <h2>{selectedProject.name}</h2>
        <p className="mt-3">{description || "No description specified."}</p>

        {url && (
          <button
            onClick={() => window.open(url, "_blank")}
            className="link-to-project mt-5"
            style={{ color: isDark ? "white" : "black" }} // boja ovisno o temi
          >
            <img src={LinkIcon} alt="icon" style={{ color: "inherit" }} />
            Link
          </button>
        )}

        <div className="project-categories mt-3 d-flex gap-3 mt-3">
          {category.map((categoryItem) => (
            <div key={categoryItem} className="category">
              {categoryItem}
            </div>
          ))}
        </div>

        <div className="software-list mt-5">
          <p>Softwares Used</p>
          <div className="d-flex gap-3 mt-3">{softwaresUsed}</div>
        </div>

        <img
          src={BackArrow}
          alt="Back"
          className="back-btn"
          onClick={() => window.history.back()}
        />

        {/* Gallery */}
        {gallery.length > 0 && (
          <MDBRow className="mt-5">
            {gallery.map((item, mediaIndex) => (
              <MDBCol key={mediaIndex} lg={12} md={12} className="mb-lg-0">
                {isVideo(item) ? (
                  <div
                    className="video-container"
                    style={{ position: "relative" }}
                  >
                    <ReactPlayer
                      url={item}
                      controls
                      className="media video"
                      playing={!!playingVideos[mediaIndex]}
                      onPlay={() => handleVideoPlay(mediaIndex)}
                      onPause={() => handleVideoPause(mediaIndex)}
                      config={{
                        file: { attributes: { preload: "metadata" } },
                      }}
                    />
                    {!playingVideos[mediaIndex] && (
                      <img
                        src={playIcon}
                        alt="Play"
                        className="play-icon hide-on-phone"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayPause(mediaIndex);
                        }}
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          cursor: "pointer",
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <img
                    src={item}
                    className="media"
                    alt={`Project image ${mediaIndex + 1}`}
                    loading="lazy"
                    onClick={() => handleMediaClick(mediaIndex)}
                    style={{ cursor: "pointer" }}
                  />
                )}
              </MDBCol>
            ))}
          </MDBRow>
        )}

        {/* YouTube video */}
        {youTubeId && (
          <div className="video-container d-flex mt-5">
            <ReactPlayer
              url={`https://www.youtube.com/embed/${youTubeId}`}
              controls
              width="50%"
              height="315px"
              className="img-fluid"
              light={true}
            />
          </div>
        )}

        <Contact />
      </div>
    </section>
  );
};

export default ViewProject;
